import { useState } from 'react'
import { ChevronDown, Settings2, Target } from 'lucide-react'
import * as Popover from '@radix-ui/react-popover'
import type { RiskProfile } from '@/lib/types'
import { RISK_PROFILES } from '@/lib/types'
import { cn } from '@/lib/utils'
import { RiskProfileModal } from './RiskProfileModal'

const MONTHS_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
]

const GOAL_PERCENT_OPTIONS = [1, 2, 3, 5, 10]

interface SpreadsheetControlsProps {
  selectedMonth: number
  selectedYear: number
  onMonthChange: (month: number) => void
  onYearChange: (year: number) => void
  riskProfile: RiskProfile
  onRiskProfileChange: (profile: RiskProfile) => void
  currentBalance: number
  goalPercent: number
  onGoalPercentChange: (percent: number) => void
}

export function SpreadsheetControls({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  riskProfile,
  onRiskProfileChange,
  currentBalance,
  goalPercent,
  onGoalPercentChange,
}: SpreadsheetControlsProps) {
  const currentYear = new Date().getFullYear()
  const years = [currentYear - 1, currentYear]
  const [isModalOpen, setIsModalOpen] = useState(false)

  const currentProfile = RISK_PROFILES[riskProfile]

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        {/* Seletor de Mês */}
        <div className="relative">
          <select
            value={selectedMonth}
            onChange={(e) => onMonthChange(Number(e.target.value))}
            className="appearance-none bg-muted/50 border border-border/50 rounded-lg px-4 py-2 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer hover:bg-muted/70 transition-colors"
          >
            {MONTHS_PT.map((month, i) => (
              <option key={i} value={i}>{month}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>

        {/* Seletor de Ano */}
        <div className="relative">
          <select
            value={selectedYear}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className="appearance-none bg-muted/50 border border-border/50 rounded-lg px-4 py-2 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer hover:bg-muted/70 transition-colors"
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>

        {/* Separador */}
        <div className="h-6 w-px bg-border/50 hidden sm:block" />

        {/* Botão de Perfil de Risco (Entrada) */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-muted/50 border border-border/50 rounded-lg px-4 py-2 text-sm text-foreground hover:bg-muted/70 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <span>{currentProfile.label}</span>
          <span className="text-xs text-muted-foreground">({currentProfile.range})</span>
        </button>

        {/* Botão de % Meta do Dia */}
        <Popover.Root>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-lg px-4 py-2 text-sm text-primary font-medium hover:bg-primary/20 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <Target className="h-4 w-4" />
              <span>Meta {goalPercent}%</span>
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align="end"
              sideOffset={6}
              className="z-50 min-w-[200px] rounded-xl border border-border bg-card p-1.5 shadow-xl animate-in fade-in-0 zoom-in-95"
            >
              <p className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                % Meta diária sobre o capital
              </p>
              {GOAL_PERCENT_OPTIONS.map((pct) => {
                const isActive = pct === goalPercent
                return (
                  <Popover.Close asChild key={pct}>
                    <button
                      type="button"
                      onClick={() => onGoalPercentChange(pct)}
                      className={cn(
                        'w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs transition-colors',
                        isActive
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-foreground hover:bg-muted/50'
                      )}
                    >
                      <span>{pct}%</span>
                      <span className="text-muted-foreground">
                        {pct <= 2 ? 'Conservador' : pct <= 5 ? 'Moderado' : 'Agressivo'}
                      </span>
                    </button>
                  </Popover.Close>
                )
              })}
              <Popover.Arrow className="fill-border" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>

      <RiskProfileModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        selectedProfile={riskProfile}
        onSelectProfile={onRiskProfileChange}
        currentBalance={currentBalance}
      />
    </>
  )
}
